package com.eladfinish.bible.contest.features.verseWidget

import com.russhwolf.settings.Settings
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlinx.serialization.json.Json
import kotlin.random.Random
import kotlin.time.Clock
import kotlin.time.Instant

/**
 * Keeps the widget's [VerseWidgetConfig] in the settings.
 *
 * The widget can be on a home screen before its screen was ever opened, so the first read stores
 * a default: its start day and seed must stay the same from then on.
 */
class VerseWidgetConfigRepository(
    private val settings: Settings,
    private val now: () -> Instant = { Clock.System.now() },
    private val timeZone: () -> TimeZone = { TimeZone.currentSystemDefault() },
) {

    fun get(): VerseWidgetConfig {
        val stored = settings.getStringOrNull(KEY)?.let { json ->
            runCatching { JSON.decodeFromString<VerseWidgetConfig>(json) }.getOrNull()
        }
        return stored?.sanitized() ?: newConfig().also(::save)
    }

    fun save(config: VerseWidgetConfig) {
        settings.putString(KEY, JSON.encodeToString(config.sanitized()))
    }

    /**
     * Applies [change] to the stored config. A change of rhythm or content starts the rotation
     * over, from today; anything else (the nikud switch) keeps the verse on screen.
     */
    fun update(change: (VerseWidgetConfig) -> VerseWidgetConfig): VerseWidgetConfig {
        val before = get()
        val after = change(before).sanitized()
        val saved = if (after.restartsRotationFrom(before)) {
            after.copy(anchorEpochDay = today(), seed = Random.nextInt(), phaseSeconds = 0, slotOffset = 0)
        } else {
            after
        }
        save(saved)
        return saved
    }

    /**
     * The user asked for a new verse: the next one of the rotation is shown now, and the time to the verse after
     * it starts over (see [VerseWidgetSlots.refreshedAt]).
     */
    fun refresh(): VerseWidgetConfig {
        val refreshed = VerseWidgetSlots.refreshedAt(now(), get(), timeZone())
        save(refreshed)
        return refreshed
    }

    /** Whether a config was ever stored, so the screen can tell "never set up" from "set up". */
    fun hasStoredConfig(): Boolean = settings.hasKey(KEY)

    private fun newConfig() = VerseWidgetConfig(
        anchorEpochDay = today(),
        seed = Random.nextInt(),
    )

    private fun today(): Long = now().toLocalDateTime(timeZone()).date.toEpochDays()

    private fun VerseWidgetConfig.sanitized(): VerseWidgetConfig {
        val interval = intervalMinutes.takeIf { it in VerseWidgetConfig.INTERVALS }
            ?: VerseWidgetConfig.DEFAULT_INTERVAL_MINUTES
        return copy(
            bookNumber = bookNumber.coerceIn(VerseWidgetConfig.FIRST_BOOK_NUMBER, VerseWidgetConfig.LAST_BOOK_NUMBER),
            intervalMinutes = interval,
            // a stored phase is one of the interval it was made for
            phaseSeconds = phaseSeconds.coerceIn(0, interval * 60 - 1),
        )
    }

    companion object {
        const val KEY = "verse_widget_config"
        private val JSON = Json {
            ignoreUnknownKeys = true
            encodeDefaults = true
        }
    }
}
